/**
 * <norse-buy-box> — variant selector + subscription/one-time selling plan UI.
 *
 * Variant at index N still syncs delivery to index N when the customer picks that pack.
 * Changing delivery alone does not change the variant (prices hydrate from product.js).
 *
 * KEY: Bundle variants have DIFFERENT selling_plan IDs than the 1-pack group used
 * in the UI. After every selection change we async-fetch product.js and resolve the
 * canonical selling_plan_id for the actual variant via slot-index mapping — exactly
 * the same strategy as selling-plans-alt.js.
 */

if (!customElements.get('norse-buy-box')) {
  // ---------------------------------------------------------------------------
  // Money helpers (match selling-plans-alt.js behaviour)
  // ---------------------------------------------------------------------------

  /**
   * Exact cents in the market's own punctuation — see NorseSellingPlanUtils.formatMoney.
   * This used to round to whole units and fall back to `Intl` with the en-US locale, which
   * on a EUR store wrote "€69" on the add-to-cart button for a line the cart charges
   * €66,95, and "SEK 774" beside Liquid's "774 kr".
   */
  function nbbFormatMoney(cents) {
    const shared = window.NorseSellingPlanUtils?.formatMoney;
    if (shared) return shared(cents);
    return (Math.round(Number(cents) || 0) / 100).toFixed(2).replace(/\.00$/, '');
  }

  /**
   * Allocation price the CART will charge. `/products/{handle}.js` disagrees with the cart
   * in every converted market — see NorseSellingPlanUtils.allocationPrice.
   */
  function nbbAllocPrice(variant, alloc, fallback) {
    const utils = window.NorseSellingPlanUtils;
    const cents = utils?.allocationPrice ? utils.allocationPrice(variant?.id, alloc) : alloc?.price;
    return cents != null ? cents : fallback;
  }

  function nbbPercentOff(compareCents, saleCents) {
    const c = Number(compareCents) || 0;
    const s = Number(saleCents) || 0;
    if (c <= s || c <= 0) return 0;
    return Math.round(((c - s) / c) * 100);
  }

  // ---------------------------------------------------------------------------
  // Canonical selling-plan resolution — shared logic in norse-selling-plan-utils.js
  // (direct id → ~40-day name match → slot-index fallback)
  // ---------------------------------------------------------------------------

  function nbbResolveCanonicalPlanId(product, variant, uiPlanId) {
    if (!uiPlanId) return '';
    const utils = window.NorseSellingPlanUtils;
    if (utils) return utils.canonicalPlanId(product, variant, uiPlanId);

    // Minimal fallback (utils script missing): direct id match only
    const uiPlanIdN = Number(uiPlanId);
    const direct = (variant.selling_plan_allocations || []).find(
      a => Number(a.selling_plan_id) === uiPlanIdN || Number(a.selling_plan?.id) === uiPlanIdN
    );
    const sid = direct?.selling_plan_id ?? direct?.selling_plan?.id;
    return sid != null ? String(sid) : String(uiPlanId);
  }

  // ---------------------------------------------------------------------------
  // Custom element
  // ---------------------------------------------------------------------------

  customElements.define(
    'norse-buy-box',
    class NorseBuyBox extends HTMLElement {
      // ── lifecycle ────────────────────────────────────────────────────────

      connectedCallback() {
        this._fetchRunId = 0;
        this._init();
      }

      // ── accessors ────────────────────────────────────────────────────────

      get sectionId() {
        const declared = this.dataset.sectionId || '';
        // If the declared section ID has a matching product form, trust it
        if (declared && document.getElementById(`product-form-${declared}`)) return declared;
        // Mismatch: the NBB was rendered in a different section context than the
        // form/gallery/button. Walk up to find the nearest ProductInfo section and
        // derive the correct ID from it.
        const infoSection = this.closest('[id^="ProductInfo-"]');
        if (infoSection) {
          const id = infoSection.id.replace('ProductInfo-', '');
          if (id && document.getElementById(`product-form-${id}`)) return id;
        }
        return declared;
      }
      get productHandle() { return this.dataset.productHandle || ''; }

      get isSubscriptionSelected() {
        return this.querySelector('[data-nbb-plan="sub"]')?.classList.contains('is-selected') ?? true;
      }

      // ── init ─────────────────────────────────────────────────────────────

      _init() {
        this._bindVariantCards();
        this._bindDeliveryOptions();
        this._bindPlanBoxes();

        // Sync initial state
        this._syncFormVariant();
        this._updateVariantCardPriceDisplay();
        this._updateBoxPrices();
        this._updateButtonText();

        // Resolve canonical selling plan ID for the initial SSR selection
        void this._resolveAndSyncPlanId();
      }

      // ── event binding ────────────────────────────────────────────────────

      _bindVariantCards() {
        this.querySelectorAll('.nbb__variant-option').forEach(card => {
          card.addEventListener('click', () => this._onVariantClick(card));
          card.addEventListener('keydown', e => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._onVariantClick(card); }
          });
        });
      }

      _bindDeliveryOptions() {
        this.querySelectorAll('.nbb__delivery-option').forEach(opt => {
          opt.addEventListener('click', e => { e.stopPropagation(); this._onDeliveryClick(opt); });
        });
      }

      _bindPlanBoxes() {
        this.querySelector('[data-nbb-plan="sub"]')?.addEventListener('click', () => this._selectPlan('sub'));
        this.querySelector('[data-nbb-plan="ot"]')?.addEventListener('click',  () => this._selectPlan('ot'));
      }

      // ── handlers ─────────────────────────────────────────────────────────

      _onVariantClick(card) {
        const idx = parseInt(card.dataset.variantIdx, 10);
        this._selectVariantCard(card);
        // Keep delivery locked to the forced-cadence plan when tagged; otherwise match variant→frequency.
        if (this.dataset.force60PlanId) {
          const forced =
            this.querySelector(`.nbb__delivery-option[data-selling-plan-id="${this.dataset.force60PlanId}"]`) ||
            this.querySelector('.nbb__delivery-option.is-selected') ||
            this.querySelector('.nbb__delivery-option');
          if (forced) this._selectDeliveryOption(forced);
        } else {
          this._selectDeliveryByIdx(idx);
        }
        this._setFormVariantId(card.dataset.variantId);
        this._updateBoxPrices();
        this._updateButtonText();
        void this._resolveAndSyncPlanId();
      }

      _onDeliveryClick(opt) {
        this._selectDeliveryOption(opt);
        // REMOVED: this._selectVariantByIdx(idx); <-- This was causing the variant to jump
        
        // Delivery selection always means subscription
        this.querySelector('[data-nbb-plan="sub"]')?.classList.add('is-selected');
        this.querySelector('[data-nbb-plan="ot"]')?.classList.remove('is-selected');
        
        this._updateVariantCardPriceDisplay();
        this._updateBoxPrices();
        this._updateButtonText();
        void this._resolveAndSyncPlanId();
      }

      // ── selection helpers ─────────────────────────────────────────────────

      _selectVariantCard(card) {
        this.querySelectorAll('.nbb__variant-option').forEach(c => c.classList.remove('is-selected'));
        card.classList.add('is-selected');
      }

      _selectDeliveryOption(opt) {
        this.querySelectorAll('.nbb__delivery-option').forEach(o => o.classList.remove('is-selected'));
        opt.classList.add('is-selected');
        const radio = opt.querySelector('.nbb__delivery-radio-input');
        if (radio) radio.checked = true;
      }

      _selectDeliveryByIdx(idx) {
        this.querySelectorAll('.nbb__delivery-option').forEach(o => {
          const hit = parseInt(o.dataset.deliveryIdx, 10) === idx;
          o.classList.toggle('is-selected', hit);
          const radio = o.querySelector('.nbb__delivery-radio-input');
          if (radio) radio.checked = hit;
        });
      }

      _selectPlan(type) {
        const isSub = type === 'sub';
        
        // 1. Update UI selection states
        this.querySelector('[data-nbb-plan="sub"]')?.classList.toggle('is-selected', isSub);
        this.querySelector('[data-nbb-plan="ot"]')?.classList.toggle('is-selected', !isSub);
        
        // 2. Refresh visuals
        this._updateVariantCardPriceDisplay();
        this._updateBoxPrices();
        this._updateButtonText();
        
        // 3. CRITICAL: Sync the selling_plan input value
        if (!isSub) {
          this._writeSellingPlanInput(''); // Clear for One-time
        } else {
          void this._resolveAndSyncPlanId(); // Fetch/Resolve ID for Sub
        }
      }

      // ── form sync ────────────────────────────────────────────────────────

      _setFormVariantId(variantId) {
        if (!variantId) return;
        const form = document.getElementById(`product-form-${this.sectionId}`);
        const idInput = form?.querySelector('input[name="id"]');
        if (!idInput) return;
        if (String(idInput.value) === String(variantId)) return;
        idInput.value = variantId;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
        document.dispatchEvent(
          new CustomEvent('variant:change', { detail: { variantId, source: 'norse-buy-box' } })
        );
      }

      _syncFormVariant() {
        const sel = this.querySelector('.nbb__variant-option.is-selected');
        if (sel?.dataset.variantId) this._setFormVariantId(sel.dataset.variantId);
      }

      /**
       * Write the selling plan hidden input.
       * planId must be the CANONICAL id for the current variant (not the UI group id).
       */
      _writeSellingPlanInput(planId) {
        const hidden = this.querySelector('[data-nbb-selling-plan-input]');
        if (!hidden) return;
        
        const val = planId ? String(planId) : '';
        hidden.value = val;
        
        // Ensure the input is connected to the correct form
        const realFormId = `product-form-${this.sectionId}`;
        if (hidden.getAttribute('form') !== realFormId) {
          hidden.setAttribute('form', realFormId);
        }

        // Trigger change so Shopify's cart scripts see the update
        hidden.dispatchEvent(new Event('change', { bubbles: true }));
        console.log(`[NBB] selling_plan set to: "${val}"`);
      }

      // ── canonical plan ID (async, race-safe) ─────────────────────────────

      /**
       * Fetch product.js, resolve the canonical selling_plan_id for the current
       * variant + delivery slot, and write it to the hidden form input.
       * One-time purchases write an empty string.
       */
      async _resolveAndSyncPlanId() {
        if (!this.isSubscriptionSelected) {
          this._writeSellingPlanInput('');
          return;
        }

        const uiPlanId =
          this.dataset.force60PlanId ||
          this.querySelector('.nbb__delivery-option.is-selected')?.dataset.sellingPlanId;
        const variantId = this.querySelector('.nbb__variant-option.is-selected')?.dataset.variantId;
        if (!uiPlanId || !variantId || !this.productHandle) {
          this._writeSellingPlanInput(uiPlanId || '');
          return;
        }

        // Write the UI plan ID immediately so the form is never empty
        this._writeSellingPlanInput(uiPlanId);

        const runId = ++this._fetchRunId;
        try {
          const res = await fetch(`${window.Shopify?.routes?.root || '/'}products/${this.productHandle}.js`);
          const product = await res.json();
          if (runId !== this._fetchRunId) return; // stale

          const variant = product.variants.find(v => String(v.id) === String(variantId));
          if (!variant) return;

          const canonical = nbbResolveCanonicalPlanId(product, variant, uiPlanId);
          this._writeSellingPlanInput(canonical);

          this._hydrateSubscriptionPricesFromProduct(product, variant);
          this._updateMaxDiscountBadge(product);
        } catch {
          // Keep the UI plan ID already written above
        }
      }

      // ── price updates ────────────────────────────────────────────────────

      _updateVariantCardPriceDisplay() {
        const isSub = this.isSubscriptionSelected;
        this.querySelectorAll('.nbb__variant-option').forEach(card => {
          card.querySelector('[data-nbb-vp-sub]')?.classList.toggle('is-hidden', !isSub);
          card.querySelector('[data-nbb-vp-ot]')?.classList.toggle('is-hidden',  isSub);
        });
      }

      _variantDeliveryIndicesAligned() {
        const card = this.querySelector('.nbb__variant-option.is-selected');
        const del = this.querySelector('.nbb__delivery-option.is-selected');
        if (!card || !del) return true;
        return parseInt(card.dataset.variantIdx, 10) === parseInt(del.dataset.deliveryIdx, 10);
      }

      _updateBoxPrices() {
        const card = this.querySelector('.nbb__variant-option.is-selected');
        if (!card) return;

        const subCents = parseInt(card.dataset.subPrice    || '0', 10); // per-unit (valid when delivery idx === variant idx)
        const otCents  = parseInt(card.dataset.otPrice     || '0', 10); // per-unit
        const cmpCents = parseInt(card.dataset.comparePrice || '0', 10); // per-unit (ref MSRP)
        const aligned = this._variantDeliveryIndicesAligned();

        // Subscription summary — DOM data assumes plan slot equals variant index; hydrate from product.js when diverged
        if (this.isSubscriptionSelected && aligned) {
          const subAmtEl = this.querySelector('[data-nbb-sub-price-amount]');
          if (subAmtEl) subAmtEl.textContent = nbbFormatMoney(subCents);

          const subCmpEl = this.querySelector('[data-nbb-sub-compare]');
          if (subCmpEl) {
            subCmpEl.textContent = nbbFormatMoney(cmpCents);
            subCmpEl.classList.toggle('is-hidden', cmpCents <= subCents);
          }
        }

        // One-time box — always reflects selected variant
        const otAmtEl = this.querySelector('[data-nbb-ot-price-amount]');
        if (otAmtEl) otAmtEl.textContent = nbbFormatMoney(otCents);

        const otCmpEl = this.querySelector('[data-nbb-ot-compare]');
        if (otCmpEl) {
          otCmpEl.textContent = nbbFormatMoney(cmpCents);
          otCmpEl.classList.toggle('is-hidden', cmpCents <= otCents);
        }

        this.querySelectorAll('.nbb__variant-option').forEach(c => {
          const cSub = parseInt(c.dataset.subPrice || '0', 10);
          const cOt  = parseInt(c.dataset.otPrice  || '0', 10);
          if (!(this.isSubscriptionSelected && !aligned)) {
            const subEl = c.querySelector('[data-nbb-vp-sub]');
            if (subEl) subEl.textContent = `${nbbFormatMoney(cSub)} each`;
          }
          const otVp = c.querySelector('[data-nbb-vp-ot]');
          if (otVp) otVp.textContent = `${nbbFormatMoney(cOt)} each`;
        });

        if (this.isSubscriptionSelected && aligned) {
          this._updateGallery(nbbPercentOff(cmpCents, subCents));
        } else if (!this.isSubscriptionSelected) {
          this._updateGallery(nbbPercentOff(cmpCents, otCents));
        }

        // --- ADD THIS AT THE END OF _updateBoxPrices() ---
        const isSub = this.isSubscriptionSelected;

        if (card) {
          const detail = {
            variantId: card.dataset.variantId,
            sellingPlanId: isSub ? this.querySelector('.nbb__delivery-option.is-selected')?.dataset.sellingPlanId : null,
            displayPrice: isSub ? 
              this.querySelector('[data-nbb-sub-price-amount]')?.innerText : 
              this.querySelector('[data-nbb-ot-price-amount]')?.innerText,
            comparePrice: isSub ? 
              this.querySelector('[data-nbb-sub-compare]')?.innerText : 
              this.querySelector('[data-nbb-ot-compare]')?.innerText,
            discount: isSub ? 
              this.querySelector('.nbb__discount-badge')?.innerText : null
          };

          // Dispatch the event that sticky-add-to-cart.liquid is listening for
          document.dispatchEvent(new CustomEvent('norse-buy-box:change', {
            detail: detail
          }));
        }
        // ------------------------------------------------
      }

      // ── button text (line total, not per-unit) ───────────────────────────

      _updateButtonText() {
        const btn = document.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        if (!btn) return;
        const btnSpan = btn.querySelector('span');
        if (!btnSpan) return;

        const card = this.querySelector('.nbb__variant-option.is-selected');
        if (!card) return;

        // data-sub-line matches the selected frequency only when variant & delivery indices match
        if (this.isSubscriptionSelected && !this._variantDeliveryIndicesAligned()) return;

        const lineCents = this.isSubscriptionSelected
          ? parseInt(card.dataset.subLine || '0', 10)
          : parseInt(card.dataset.otLine  || '0', 10);

        if (lineCents > 0) {
          btnSpan.textContent = `ADD TO CART - ${nbbFormatMoney(lineCents)}`;
        }
      }

      // ── gallery & badges ─────────────────────────────────────────────────

      _updateGallery(savePct) {
        const gallery = document.getElementById(`MediaGallery-${this.sectionId}`);
        if (gallery && typeof gallery.setSavePercent === 'function') {
          gallery.setSavePercent(savePct);
        }
      }

      /**
       * Recompute the max discount badge from fresh product JSON (per-unit vs ref MSRP).
       */
      _updateMaxDiscountBadge(product) {
        const badge = this.querySelector('[data-nbb-max-discount]');
        if (!badge) return;

        const refMsrp = this._getRefMsrpPerUnit(product);
        let maxPct = 0;
        product.variants.forEach(v => {
          const kit = this._kitCount(v);
          (v.selling_plan_allocations || []).forEach(alloc => {
            const subPu = (alloc.price || 0) / kit;
            const cmp = refMsrp > 0 ? refMsrp : ((v.compare_at_price || v.price || 0) / kit);
            const pct = nbbPercentOff(cmp, subPu);
            if (pct > maxPct) maxPct = pct;
          });
        });
        if (maxPct > 0) badge.textContent = `UP TO ${maxPct}%`;
      }

      _deliverySlotIndex(product, uiPlanId) {
        const primaryPlans = product.selling_plan_groups?.[0]?.selling_plans || [];
        const idx = primaryPlans.findIndex(p => String(p.id) === String(uiPlanId));
        return idx >= 0 ? idx : 0;
      }

      _updateGalleryFromProduct(product, variant) {
        const isSub = this.isSubscriptionSelected;
        const refMsrp = this._getRefMsrpPerUnit(product);
        const kit = this._kitCount(variant);

        const uiPlanId = this.querySelector('.nbb__delivery-option.is-selected')?.dataset.sellingPlanId;
        const slotIdx = this._deliverySlotIndex(product, uiPlanId);
        const allocs = variant.selling_plan_allocations || [];
        const alloc = allocs[slotIdx] ?? allocs[0];

        const cmp = refMsrp > 0 ? refMsrp : ((variant.compare_at_price || variant.price || 0) / kit);
        const subPu = nbbAllocPrice(variant, alloc, variant.price) / kit;
        const otPu  = variant.price / kit;

        const savePct = isSub ? nbbPercentOff(cmp, subPu) : nbbPercentOff(cmp, otPu);
        this._updateGallery(savePct);
      }

      /** Subscription header prices + ATC line when plan slot differs from variant index */
      _hydrateSubscriptionPricesFromProduct(product, variant) {
        if (!this.isSubscriptionSelected) return;

        const uiPlanId = this.querySelector('.nbb__delivery-option.is-selected')?.dataset.sellingPlanId;
        if (!uiPlanId) return;

        const refMsrp = this._getRefMsrpPerUnit(product);
        const kit = this._kitCount(variant);
        const slotIdx = this._deliverySlotIndex(product, uiPlanId);
        const allocs = variant.selling_plan_allocations || [];
        const alloc = allocs[slotIdx] ?? allocs[0];

        const lineRaw = nbbAllocPrice(variant, alloc, variant.price);
        const lineCents = Math.round(Number(lineRaw));
        const subPuCents = Math.round(lineRaw / kit);

        const cmp = refMsrp > 0 ? refMsrp : ((variant.compare_at_price || variant.price || 0) / kit);
        const cmpPuCents = Math.round(cmp);

        const subAmtEl = this.querySelector('[data-nbb-sub-price-amount]');
        if (subAmtEl) subAmtEl.textContent = nbbFormatMoney(subPuCents);

        const subCmpEl = this.querySelector('[data-nbb-sub-compare]');
        if (subCmpEl) {
          subCmpEl.textContent = nbbFormatMoney(cmpPuCents);
          subCmpEl.classList.toggle('is-hidden', cmpPuCents <= subPuCents);
        }

        const btn = document.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        const btnSpan = btn?.querySelector('span');
        if (btnSpan && lineCents > 0) {
          btnSpan.textContent = `ADD TO CART - ${nbbFormatMoney(lineCents)}`;
        }

        this._updateGalleryFromProduct(product, variant);
        this._refreshVariantCardsSubPricesForSlot(product, slotIdx);
      }

      _refreshVariantCardsSubPricesForSlot(product, slotIdx) {
        if (!this.isSubscriptionSelected) return;
        this.querySelectorAll('.nbb__variant-option').forEach(card => {
          const vid = card.dataset.variantId;
          const v = product.variants.find(x => String(x.id) === String(vid));
          if (!v) return;
          const k = this._kitCount(v);
          const allocs = v.selling_plan_allocations || [];
          const alloc = allocs[slotIdx] ?? allocs[0];
          const subPuCents = Math.round(nbbAllocPrice(v, alloc, v.price) / k);
          const el = card.querySelector('[data-nbb-vp-sub]');
          if (el) el.textContent = `${nbbFormatMoney(subPuCents)} each`;
        });
      }

      // ── helpers ──────────────────────────────────────────────────────────

      _kitCount(variant) {
        const first = parseInt(String(variant?.title || '').split(/\s+/)[0], 10);
        if (Number.isFinite(first) && first >= 1) return first;
        return Math.max(1, variant?.quantity_rule?.min || 1);
      }

      _getRefMsrpPerUnit(product) {
        const ref = (product.variants || []).find(v => this._kitCount(v) === 1);
        return ref ? (ref.compare_at_price || ref.price || 0) : 0;
      }
    }
  );
}
